import { useState } from "react";
import { Mail, MapPin, Phone, Send, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";

const SUBJECT_KEYS = ["general", "bulk", "coa", "collaboration", "press"];

export default function Contact() {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: t("contact.subjects.general"),
        message: "",
    });
    const [status, setStatus] = useState({ loading: false, success: false, error: "" });

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, success: false, error: "" });
        try {
            await api.post("/contact", form);
            setStatus({ loading: false, success: true, error: "" });
            setForm({ name: "", email: "", subject: t("contact.subjects.general"), message: "" });
        } catch (e) {
            setStatus({ loading: false, success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
        }
    };

    return (
        <div data-testid="contact-page" className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-20">
                <div className="lg:col-span-5">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("contact.eyebrow")}</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold uppercase tracking-tighter">
                        {t("contact.titleLine1")}
                        <br />
                        {t("contact.titleLine2")}
                    </h1>
                    <p className="mt-6 sm:mt-8 text-neutral-600 leading-relaxed text-sm sm:text-base">
                        {t("contact.intro")}
                    </p>

                    <div className="mt-8 sm:mt-12 space-y-px bg-white/5 rounded-xl overflow-hidden">
                        <Info icon={Mail} label={t("contact.infoEmail")} value="info@nexuspeptides.online" />
                        <Info icon={Phone} label={t("contact.infoPhone")} value="+1 (415) 555-0182" />
                        <Info icon={MapPin} label={t("contact.infoAddress")} value="2400 Bayshore Pkwy · San Francisco, CA 94110" />
                    </div>

                    <div className="mt-8 sm:mt-10 border border-[#00aeef]/30 bg-[#f8f8f8]/5 p-4 sm:p-5 rounded-lg">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] mb-2">{t("contact.opsEyebrow")}</div>
                        <div className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">
                            {t("contact.ops")}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <form onSubmit={submit} className="border border-neutral-200 p-5 sm:p-8 lg:p-10 rounded-xl">
                        <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold uppercase tracking-tight mb-6 sm:mb-8">{t("contact.formTitle")}</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                            <Field label={`${t("contact.name")} *`}>
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="contact-name"
                                />
                            </Field>
                            <Field label={`${t("contact.email")} *`}>
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="contact-email"
                                />
                            </Field>
                        </div>

                        <div className="mb-5">
                            <Field label={`${t("contact.subject")} *`}>
                                <select
                                    required
                                    value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                    className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="contact-subject"
                                >
                                    {SUBJECT_KEYS.map((k) => (
                                        <option key={k}>{t(`contact.subjects.${k}`)}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        <div className="mb-6">
                            <Field label={`${t("contact.message")} *`}>
                                <textarea
                                    required
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    rows={6}
                                    className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900 resize-none"
                                    data-testid="contact-message"
                                />
                            </Field>
                        </div>

                        {status.error && (
                            <div className="mb-4 border border-red-500/40 bg-red-500/10 text-red-700 text-xs p-3" data-testid="contact-error">
                                {status.error}
                            </div>
                        )}
                        {status.success && (
                            <div className="mb-4 border border-[#00aeef]/40 bg-neutral-100 text-neutral-900 text-xs p-3 flex items-center gap-2" data-testid="contact-success">
                                <CheckCircle2 className="w-4 h-4" /> {t("contact.success")}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={status.loading}
                            className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#f8f8f8] hover:bg-neutral-800 rounded-lg disabled:opacity-50 text-black font-bold uppercase tracking-[0.18em] text-xs px-7 py-4 transition-colors"
                            data-testid="contact-submit"
                        >
                            <Send className="w-4 h-4" />
                            {status.loading ? t("contact.sending") : t("contact.send")}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function Info({ icon: Icon, label, value }) {
    return (
        <div className="bg-white p-5 flex items-start gap-4">
            <Icon className="w-5 h-5 text-[#0077b6] mt-1" />
            <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">{label}</div>
                <div className="text-sm text-neutral-800 mt-0.5">{value}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{label}</label>
            {children}
        </div>
    );
}
